import React, { useEffect, useState, useCallback } from "react";
import productsData from "../json/products.json";
import "./css/ProductsShowcase.css";

const ProductsShowcase = ({ productId, onNavigate }) => {
  const [product, setProduct]           = useState(null);
  const [activeIndex, setActiveIndex]   = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [autoplay, setAutoplay]         = useState(true);

  useEffect(() => {
    const selected = productsData.find((p) => p.id === productId);
    setProduct(selected);
    setActiveIndex(0);
    setAutoplay(true);
  }, [productId]);

  const hasImages = product?.images?.length > 0;

  const prev = useCallback(() => {
    setAutoplay(false);
    setActiveIndex((i) => (i === 0 ? product.images.length - 1 : i - 1));
  }, [product]);

  const next = useCallback(() => {
    setAutoplay(false);
    setActiveIndex((i) => (i + 1) % product.images.length);
  }, [product]);

  useEffect(() => {
    if (!hasImages || !autoplay) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % product.images.length);
    }, 3500);
    return () => clearInterval(id);
  }, [product, hasImages, autoplay]);

  useEffect(() => {
    const handler = (e) => {
      if (!isViewerOpen) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") setIsViewerOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isViewerOpen, prev, next]);

  if (!product) {
    return (
      <div className="sc-error">
        <p>Product not found.</p>
        <button onClick={() => onNavigate("home")}>Back to Home</button>
      </div>
    );
  }

  const statusClass =
    product.status === "Active" ? "active" :
    product.status === "In Testing" ? "testing" : "development";

  const mainImage = hasImages ? product.images[activeIndex] : null;

  return (
    <div className="sc-page">

      {/* ── Hero strip ── */}
      <div className="sc-hero">
        <div className="sc-hero-inner">
          <button className="sc-back" onClick={() => onNavigate("products")}>
            ← Back to Apps
          </button>
          <div className="sc-hero-text">
            <div className="sc-title-row">
              <h1>{product.name}</h1>
              <span className={`sc-badge ${statusClass}`}>{product.status}</span>
            </div>
            {product.tagline && <p className="sc-tagline">{product.tagline}</p>}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="sc-body">
        <div className="sc-layout">

          {/* ── Gallery ── */}
          <div className="sc-gallery">
            {hasImages ? (
              <>
                <div className="sc-main-wrap">
                  <img
                    src={mainImage}
                    alt={product.name}
                    className="sc-main-img"
                    onClick={() => setIsViewerOpen(true)}
                  />
                  {product.images.length > 1 && (
                    <>
                      <button className="sc-arrow sc-arrow-left" onClick={prev} aria-label="Previous">
                        ‹
                      </button>
                      <button className="sc-arrow sc-arrow-right" onClick={next} aria-label="Next">
                        ›
                      </button>
                    </>
                  )}
                  <span className="sc-img-counter">{activeIndex + 1} / {product.images.length}</span>
                </div>

                {product.images.length > 1 && (
                  <div className="sc-thumbs">
                    {product.images.map((img, idx) => (
                      <button
                        key={idx}
                        className={`sc-thumb${idx === activeIndex ? " active" : ""}`}
                        onClick={() => { setAutoplay(false); setActiveIndex(idx); }}
                        aria-label={`Image ${idx + 1}`}
                      >
                        <img src={img} alt={`${product.name} ${idx + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="sc-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <p>Preview Coming Soon</p>
                <span>This app is currently in development</span>
              </div>
            )}
          </div>

          {/* ── Info panel ── */}
          <aside className="sc-info">
            <div className="sc-info-inner">
              <div className="sc-section">
                <h2 className="sc-section-title">About</h2>
                <p className="sc-description">{product.description}</p>
              </div>

              <div className="sc-divider" />

              <div className="sc-section">
                <h2 className="sc-section-title">Status</h2>
                <div className={`sc-status-pill ${statusClass}`}>
                  <span className="sc-status-dot" />
                  {product.status}
                </div>
              </div>

              {Array.isArray(product.url) && product.url.length > 0 && (
                <>
                  <div className="sc-divider" />
                  <div className="sc-section">
                    <h2 className="sc-section-title">Visit</h2>
                    <div className="sc-links">
                      {product.url.map((link, idx) => {
                        let label;
                        if (product.id === "vyral-social") {
                          label = ["OFRL", "OFETH", "OBTC"][idx] || `Link ${idx + 1}`;
                        } else {
                          label = product.name;
                        }
                        return (
                          <a
                            key={idx}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sc-link-btn"
                          >
                            Visit {label} ↗
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </aside>

        </div>
      </div>

      {/* ── Lightbox ── */}
      {isViewerOpen && hasImages && (
        <div className="sc-lightbox" onClick={() => setIsViewerOpen(false)}>
          <button className="sc-lightbox-close" onClick={() => setIsViewerOpen(false)}>×</button>
          {product.images.length > 1 && (
            <>
              <button className="sc-lightbox-arrow left" onClick={(e) => { e.stopPropagation(); prev(); }}>‹</button>
              <button className="sc-lightbox-arrow right" onClick={(e) => { e.stopPropagation(); next(); }}>›</button>
            </>
          )}
          <img
            src={mainImage}
            alt={product.name}
            className="sc-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="sc-lightbox-counter">{activeIndex + 1} / {product.images.length}</span>
        </div>
      )}
    </div>
  );
};

export default ProductsShowcase;
